export type ShelfVarianceTone = "up" | "down" | "flat" | "missing";

export type ShelfVariance = {
  delta_usd: number;
  /** Change as a percent of the shelf price. Null when the shelf price is zero. */
  percent: number | null;
  label: string;
  title: string;
  tone: ShelfVarianceTone;
};

function round2(amount: number): number {
  return Math.round(amount * 100) / 100;
}

function usd(amount: number): string {
  return amount.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

function signedUsd(amount: number): string {
  const body = usd(Math.abs(amount));
  if (amount > 0) return `+${body}`;
  if (amount < 0) return `−${body}`;
  return body;
}

/** Calculated sum compared with the price currently on the shelf. */
export function shelfVariance(calculated: number, shelf: number | null | undefined): ShelfVariance {
  if (shelf == null || !Number.isFinite(shelf)) {
    return {
      delta_usd: 0,
      percent: null,
      label: "No shelf price",
      title: "This product has no shelf price yet.",
      tone: "missing",
    };
  }
  const delta = round2(calculated - shelf);
  const percent = shelf > 0 ? round2((delta / shelf) * 100) : null;
  const tone: ShelfVarianceTone = delta > 0 ? "up" : delta < 0 ? "down" : "flat";
  if (tone === "flat") {
    return {
      delta_usd: 0,
      percent: 0,
      label: "Matches shelf",
      title: `Shelf price ${usd(shelf)} matches this sum.`,
      tone,
    };
  }
  const pct = percent == null ? "" : ` · ${delta > 0 ? "+" : "−"}${Math.abs(percent).toFixed(1)}%`;
  const direction = delta > 0 ? "above" : "below";
  return {
    delta_usd: delta,
    percent,
    label: `${signedUsd(delta)}${pct}`,
    title: `Shelf ${usd(shelf)}. This sum is ${usd(Math.abs(delta))} ${direction} that price.`,
    tone,
  };
}
